/**
 * The system context the run figures are read against.
 *
 * TWO FACTS, BOTH OF THEM SOURCED. The connection state comes from
 * `GET /api/admin/bexio/status`; the timezone is the app's own `ZURICH_TZ`.
 * Nothing here is derived from the hostname, from `NODE_ENV` or from a JWT claim.
 *
 * TWO, NOT THREE, AND DELIBERATELY SO. A "letzter erfolgreicher Sync" chip would
 * be the third — and it would repeat, a few hundred pixels away, the figure the
 * first KPI card already carries, with no context the card does not have. A strip
 * that is symmetrical because a redundant chip was added to fill the third slot
 * is worse than a short one: the reader has to work out whether the two values
 * can ever disagree. They cannot; there is only one of them, and it lives in the
 * KPI row.
 *
 * WHAT THE REFERENCE SHOWS AND THIS DOES NOT: a tenant chip and an environment
 * chip — the two that would have filled the gap. `BexioStatusResponse` carries `connection`, `counts` and
 * `last_successful_sync` — no organisation name and no environment — and the
 * token the browser holds does not name one either. Reading "Produktion" off
 * `window.location.hostname` would be the page guessing, and an operator would
 * have no way to tell the guess from a fact. So those two chips are absent until
 * something real backs them.
 *
 * The connection wording is the workspace's existing mapping (`connectionTone` +
 * `CONNECTION_STATUS_LABEL`), so this strip and the overview tile cannot end up
 * describing the same connection with different words. It is also labelled
 * "Bexio-Verbindung" rather than "API-Status": the value is the stored state of
 * the connection record, not a live ping.
 */
import React from "react";
import { Globe, Link2 } from "lucide-react";

import {
  CONNECTION_STATUS_LABEL,
  connectionTone,
  mapLabel,
} from "../../../components/admin/bexio/bexioFormat";
import { ZURICH_TZ } from "../../../utils/datetime";

const DOT = {
  good: "bg-emerald-500",
  bad: "bg-red-500",
  warn: "bg-amber-500",
  neutral: "bg-muted-foreground/50",
};

/**
 * One labelled fact. The label stays visible; the icon only decorates.
 *
 * `dot` is a sibling of the value rather than a child of it, so the value stays
 * a single text node — a status word split across elements is a word a screen
 * reader and a text query both have to reassemble.
 */
function MetaChip({ icon: Icon, label, dot, children }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      {dot ? (
        <span
          aria-hidden="true"
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`}
        />
      ) : null}
      <span className="min-w-0 truncate text-xs font-medium text-foreground">
        {children}
      </span>
    </div>
  );
}

export default function BexioSyncRunsMetaStrip({ status, statusLoading, statusError }) {
  const connection = status?.connection;
  const tone = connection?.configured ? connectionTone(connection.status) : "warn";

  /* The status read fails on its own (see the page docstring), so the connection
     chip says so and the timezone stays — it depends on no response at all. The
     KPI card that also reads `/status` keeps its own failure copy; one broken
     read must not blank two unrelated things. */
  const connectionText = statusLoading
    ? "…"
    : statusError
    ? "Nicht verfügbar"
    : connection?.configured
    ? mapLabel(CONNECTION_STATUS_LABEL, connection.status)
    : "Nicht konfiguriert";

  return (
    <section
      aria-label="Systemkontext"
      className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border bg-card px-4 py-2.5"
    >
      <MetaChip
        icon={Link2}
        label="Bexio-Verbindung"
        dot={statusLoading || statusError ? DOT.neutral : DOT[tone] || DOT.neutral}
      >
        {connectionText}
      </MetaChip>

      <MetaChip icon={Globe} label="Zeitzone">
        {ZURICH_TZ}
      </MetaChip>
    </section>
  );
}
